import React, { useEffect, useState } from 'react';
import { hämtaAnbudskalender, skapaAnbudspost, taBortAnbudspost, AnbudskalenderPost } from '../api/client';
import { TEMA } from '../theme';

const KATEGORI_INFO: Record<string, { färg: string; namn: string }> = {
  sortimentsskifte: { färg: TEMA.positive, namn: 'Sortimentsskifte' },
  offertstopp: { färg: TEMA.accent, namn: 'Offertstopp' },
  lanseringsprovstopp: { färg: '#BA7517', namn: 'Lanseringsprovstopp' },
  undantagsvecka: { färg: TEMA.inkFaint, namn: 'Undantagsvecka' },
};

const SORTIMENTSTYP_ALTERNATIV = ['fast', 'tsv', 'tst', 'tsls', 'tss', 'bs'];

const MÅNAD_ÅR = new Intl.DateTimeFormat('sv-SE', { month: 'long', year: 'numeric' });
const DAG_MÅNAD = new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short' });

function kapitaliseraFörstaBokstav(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formateraDatum(startStr: string, slutStr?: string | null) {
  const start = new Date(startStr);
  if (!slutStr || slutStr === startStr) {
    return DAG_MÅNAD.format(start);
  }
  const slut = new Date(slutStr);
  const sammaMånad = start.getMonth() === slut.getMonth() && start.getFullYear() === slut.getFullYear();
  if (sammaMånad) {
    return `${start.getDate()}–${DAG_MÅNAD.format(slut)}`;
  }
  return `${DAG_MÅNAD.format(start)} – ${DAG_MÅNAD.format(slut)}`;
}

const tomHändelseUtkast = { titel: '', kategori: 'sortimentsskifte', sortimentstyp: '', datum_start: '', datum_slut: '' };

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: TEMA.inkFaint,
  margin: '0 0 6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimärStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.accent}`,
  background: TEMA.accent,
  color: TEMA.onAccent,
  cursor: 'pointer',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

export default function Anbudskalender() {
  const [poster, setPoster] = useState<AnbudskalenderPost[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);

  // Ny händelse
  const [visaNyHändelse, setVisaNyHändelse] = useState(false);
  const [nyHändelseUtkast, setNyHändelseUtkast] = useState(tomHändelseUtkast);
  const [skaparHändelse, setSkaparHändelse] = useState(false);
  const [nyHändelseFel, setNyHändelseFel] = useState<string | null>(null);

  // Ta bort
  const [raderarId, setRaderarId] = useState<string | null>(null);
  const [raderaFel, setRaderaFel] = useState<string | null>(null);

  useEffect(() => {
    hämtaData();
  }, []);

  function hämtaData() {
    setLaddar(true);
    setFel(null);
    hämtaAnbudskalender()
      .then((res) => {
        setPoster(res.data);
        setLaddar(false);
      })
      .catch((err) => {
        console.error('Kunde inte ladda anbudskalendern:', err);
        setFel('Kunde inte ladda anbudskalendern just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }

  function öppnaNyHändelse() {
    setNyHändelseUtkast(tomHändelseUtkast);
    setNyHändelseFel(null);
    setVisaNyHändelse(true);
  }

  function avbrytNyHändelse() {
    setVisaNyHändelse(false);
    setNyHändelseFel(null);
  }

  async function sparaNyHändelse() {
    if (!nyHändelseUtkast.titel.trim()) {
      setNyHändelseFel('Titel krävs.');
      return;
    }
    if (!nyHändelseUtkast.datum_start) {
      setNyHändelseFel('Startdatum krävs.');
      return;
    }
    setSkaparHändelse(true);
    setNyHändelseFel(null);
    try {
      await skapaAnbudspost({
        titel: nyHändelseUtkast.titel.trim(),
        kategori: nyHändelseUtkast.kategori as AnbudskalenderPost['kategori'],
        sortimentstyp: (nyHändelseUtkast.sortimentstyp || null) as AnbudskalenderPost['sortimentstyp'],
        datum_start: nyHändelseUtkast.datum_start,
        datum_slut: nyHändelseUtkast.datum_slut || null,
      });
      setVisaNyHändelse(false);
      hämtaData();
    } catch (err) {
      console.error('Kunde inte skapa händelse:', err);
      setNyHändelseFel('Kunde inte spara händelsen. Försök igen.');
    } finally {
      setSkaparHändelse(false);
    }
  }

  async function taBort(post: AnbudskalenderPost) {
    if (!post.id) return;
    const bekräftat = window.confirm(`Ta bort "${post.titel}"? Detta påverkar kalendern för alla kunder.`);
    if (!bekräftat) return;
    setRaderarId(post.id);
    setRaderaFel(null);
    try {
      await taBortAnbudspost(post.id);
      setPoster(prev => prev.filter(p => p.id !== post.id));
    } catch (err) {
      console.error('Kunde inte ta bort händelse:', err);
      setRaderaFel('Kunde inte ta bort händelsen. Försök igen.');
    } finally {
      setRaderarId(null);
    }
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>;

  const sorterade = [...poster].sort(
    (a, b) => new Date(a.datum_start).getTime() - new Date(b.datum_start).getTime()
  );

  const grupper: { nyckel: string; etikett: string; poster: AnbudskalenderPost[] }[] = [];
  for (const post of sorterade) {
    const d = new Date(post.datum_start);
    const nyckel = `${d.getFullYear()}-${d.getMonth()}`;
    let grupp = grupper.find(g => g.nyckel === nyckel);
    if (!grupp) {
      grupp = { nyckel, etikett: kapitaliseraFörstaBokstav(MÅNAD_ÅR.format(d)), poster: [] };
      grupper.push(grupp);
    }
    grupp.poster.push(post);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: 0 }}>Kalender</h1>
        {!visaNyHändelse && (
          <button onClick={öppnaNyHändelse} style={btnPrimärStyle}>
            + Lägg till händelse
          </button>
        )}
      </div>

      {visaNyHändelse && (
        <div style={{
          background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px',
          padding: '22px 24px', marginBottom: '28px',
        }}>
          {nyHändelseFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 12px', fontFamily: "'Inter', sans-serif" }}>{nyHändelseFel}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <p style={labelStyle}>Titel</p>
              <input
                type="text"
                placeholder="T.ex. Offertstopp — rött vin, TSV"
                value={nyHändelseUtkast.titel}
                onChange={e => setNyHändelseUtkast(u => ({ ...u, titel: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <p style={labelStyle}>Kategori</p>
              <select
                value={nyHändelseUtkast.kategori}
                onChange={e => setNyHändelseUtkast(u => ({ ...u, kategori: e.target.value }))}
                style={inputStyle}
              >
                {Object.entries(KATEGORI_INFO).map(([nyckel, info]) => (
                  <option key={nyckel} value={nyckel}>{info.namn}</option>
                ))}
              </select>
            </div>
            <div>
              <p style={labelStyle}>Sortimentstyp</p>
              <select
                value={nyHändelseUtkast.sortimentstyp}
                onChange={e => setNyHändelseUtkast(u => ({ ...u, sortimentstyp: e.target.value }))}
                style={inputStyle}
              >
                <option value="">— Ingen —</option>
                {SORTIMENTSTYP_ALTERNATIV.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <p style={labelStyle}>Startdatum</p>
              <input
                type="date"
                value={nyHändelseUtkast.datum_start}
                onChange={e => setNyHändelseUtkast(u => ({ ...u, datum_start: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <p style={labelStyle}>Slutdatum (valfritt)</p>
              <input
                type="date"
                value={nyHändelseUtkast.datum_slut}
                onChange={e => setNyHändelseUtkast(u => ({ ...u, datum_slut: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={sparaNyHändelse} disabled={skaparHändelse} style={{ ...btnPrimärStyle, opacity: skaparHändelse ? 0.6 : 1 }}>
              {skaparHändelse ? 'Sparar...' : 'Spara'}
            </button>
            <button onClick={avbrytNyHändelse} disabled={skaparHändelse} style={btnGhostStyle}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {raderaFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 20px', fontFamily: "'Inter', sans-serif" }}>{raderaFel}</p>}

      {sorterade.length === 0 ? (
        <p style={{ color: TEMA.inkFaint, textAlign: 'center', marginTop: '48px', fontFamily: "'Inter', sans-serif" }}>
          Inga händelser i anbudskalendern än.
        </p>
      ) : (
        <>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '20px', margin: '0 0 28px',
            padding: '14px 20px', background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px',
          }}>
            {Object.entries(KATEGORI_INFO).map(([nyckel, info]) => (
              <div key={nyckel} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: info.färg, flexShrink: 0 }} />
                {info.namn}
              </div>
            ))}
          </div>

          <div style={{ position: 'relative', paddingLeft: '22px' }}>
            <div style={{ position: 'absolute', left: '7px', top: '6px', bottom: '6px', width: '1px', background: TEMA.border }} />

            {grupper.map((grupp, gIdx) => (
              <div key={grupp.nyckel} style={{ marginBottom: gIdx < grupper.length - 1 ? '28px' : 0 }}>
                <p style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink, margin: '0 0 12px', position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: '-22px', top: '6px',
                    width: '9px', height: '9px', borderRadius: '50%', background: TEMA.inkSoft,
                  }} />
                  {grupp.etikett}
                </p>
                {grupp.poster.map((post, pIdx) => {
                  const info = KATEGORI_INFO[post.kategori] || { färg: TEMA.inkFaint, namn: post.kategori };
                  const sistaIGruppen = pIdx === grupp.poster.length - 1;
                  const raderar = raderarId === post.id;
                  return (
                    <div
                      key={post.id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0',
                        borderBottom: sistaIGruppen ? 'none' : `1px solid ${TEMA.border}`,
                      }}
                    >
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: info.färg, marginTop: '7px', flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
                          <span style={{ fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{post.titel}</span>
                          <span style={{ fontSize: '12px', color: TEMA.inkFaint, whiteSpace: 'nowrap', fontFamily: "'IBM Plex Mono', monospace" }}>
                            {formateraDatum(post.datum_start, post.datum_slut)}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{info.namn}</span>
                      </div>
                      <button
                        onClick={() => taBort(post)}
                        disabled={raderar}
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: '11px', padding: '4px 10px', borderRadius: '2px',
                          border: `1px solid ${TEMA.border}`, background: 'transparent', cursor: raderar ? 'default' : 'pointer',
                          color: TEMA.inkFaint, opacity: raderar ? 0.6 : 1, flexShrink: 0, marginTop: '2px',
                        }}
                      >
                        {raderar ? '...' : 'Ta bort'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}